import type { ReactNode } from "react";
import type { BetaRequest, BetaState, Submission } from "../api/types";
import { fmtDate } from "../lib/format";
import { DOCS, MINORS_PROMPT, SOCIAL_PROMPT, TERMS_PROMPT } from "../lib/legal";
import { DocLink, Words } from "./Legal";
import { Dot, Seg } from "./ui";

/**
 * The Player Spotlight beta questionnaire (owner, 2026-10-22; canvas in docs/design/mocks/beta-intake/;
 * API services/beta.ts), used by pages/Beta.tsx. Three numbered steps: Your video (the file and "Anyone
 * under 18 in this video?", with the guardian's agreement on Yes), Your scorecard (the link and the
 * participant's name on it, both required), social media (an optional separate tick, off on every upload,
 * and a handle to tag them). The first upload, and the first since the beta terms changed, ends with one
 * sentence on the licence and the terms tick. The words beside each tick come from
 * packages/legal/manifest.json, the file the API records each yes against.
 */

/** What the participant chose. `minors` has no answer until they pick one; `name` starts as their Google name. */
export interface BetaChoice { minors: "" | "no" | "yes"; guardian: boolean; scorecard: string; name: string; social: boolean; handle: string; terms: boolean }

export const newChoice = (name: string): BetaChoice => ({ minors: "", guardian: false, scorecard: "", name, social: false, handle: "", terms: false });

export type BetaGap = "file" | "minors" | "guardian" | "scorecard" | "name" | "social" | "handle" | "terms";

/** The element Submit brings into view for each gap. */
export const GAP_ID: Record<BetaGap, string> = { file: "bq-file", minors: "bq-minors", guardian: "bq-guardian", scorecard: "bq-score", name: "bq-name", social: "bq-social", handle: "bq-handle", terms: "bq-terms" };

/** Instagram's rule for a username, as the API checks it. */
const HANDLE = /^[A-Za-z0-9._]{1,30}$/;
const cleanHandle = (h: string) => h.trim().replace(/^@/, "");

function webLink(s: string): boolean {
  try {
    const u = new URL(s);
    return (u.protocol === "https:" || u.protocol === "http:") && !!u.hostname;
  } catch {
    return false;
  }
}

/** The first word of a Google name ("Sid Nair" -> "Sid"); the part before the @ of an address. */
export function firstName(displayName: string | undefined): string {
  const first = (displayName ?? "").trim().split(/\s+/)[0] ?? "";
  return first.includes("@") ? first.split("@")[0]! : first;
}

/**
 * What stops the submission, in page order. `full`: the steps after the file are asked (an interrupted
 * upload that resumes sent its answers when it started, so only the file is asked).
 */
export function betaGaps(c: BetaChoice, hasFile: boolean, full: boolean, needsTerms: boolean): BetaGap[] {
  const out: BetaGap[] = [];
  if (!hasFile) out.push("file");
  if (!full) return out;
  if (c.minors === "") out.push("minors");
  if (c.minors === "yes" && !c.guardian) out.push("guardian");
  const link = c.scorecard.trim();
  if (!link || !webLink(link) || link.length > 500) out.push("scorecard");
  if (!c.name.trim()) out.push("name");
  const handle = cleanHandle(c.handle);
  if (c.social && handle && !HANDLE.test(handle)) out.push("handle");
  if (needsTerms && !c.terms) out.push("terms");
  return out;
}

/** The one line beside the Submit button. */
export function gapsMessage(gaps: BetaGap[], c: BetaChoice): string {
  const linkGiven = !!c.scorecard.trim();
  const asks: string[] = [];
  if (gaps.includes("file")) asks.push("choose your video");
  if (gaps.includes("minors")) asks.push(`answer "${MINORS_PROMPT.question}"`);
  if (gaps.includes("guardian")) asks.push("confirm a parent or guardian agreed");
  if (gaps.includes("scorecard") && !linkGiven) asks.push("add the scorecard link");
  if (gaps.includes("name")) asks.push("add your name as it is on the scorecard");
  if (gaps.includes("terms")) asks.push("agree to the Terms of use");
  const lines: string[] = [];
  if (asks.length) {
    const s = asks.length > 1 ? `${asks.slice(0, -1).join(", ")} and ${asks[asks.length - 1]}` : asks[0]!;
    lines.push(`${s.charAt(0).toUpperCase()}${s.slice(1)} to submit.`);
  }
  if (gaps.includes("scorecard") && linkGiven) lines.push("Check the scorecard link: it must be a web address (https://…).");
  if (gaps.includes("handle")) lines.push("An Instagram handle has only letters, numbers, full stops and underscores.");
  return lines.join(" ");
}

/** What goes to `POST /v1/uploads` as `beta`. Call it only when `betaGaps` is empty. */
export function betaRequest(c: BetaChoice, needsTerms: boolean): BetaRequest {
  return {
    ...(needsTerms ? { accept_terms: { version: DOCS.beta_terms.version } } : {}),
    minors: c.minors === "yes" ? "yes" : "no",
    guardian_confirmed: c.minors === "yes" && c.guardian,
    scorecard_url: c.scorecard.trim(),
    scorecard_name: c.name.trim(),
    social: c.social ? { prompt_id: "beta.social.v6", instagram_handle: cleanHandle(c.handle) } : null,
  };
}

/** The terms tick shows on the first upload, and again once a new major version is live. */
export const needsTermsTick = (state: BetaState | undefined) => !state?.terms_current;

/** One numbered step of the questionnaire. */
export function Step({ n, title, optional, testid, children }: { n: number; title: string; optional?: boolean; testid: string; children: ReactNode }) {
  return (
    <section className="bq-step" data-testid={testid}>
      <h2><span className="bq-n" aria-hidden="true">{n}</span>{title}{optional && <span className="opt">optional</span>}</h2>
      {children}
    </section>
  );
}

type Part = { choice: BetaChoice; onChange: (c: BetaChoice) => void; gaps: BetaGap[] };

/** "Anyone under 18 in this video?", and the parent's or guardian's agreement on Yes (terms of use 3.3). */
export function MinorsQuestion({ choice, onChange, gaps }: Part) {
  const set = (p: Partial<BetaChoice>) => onChange({ ...choice, ...p });
  return (
    <>
      <h3 className="bq-q" id="bq-minors">{MINORS_PROMPT.question}</h3>
      <div className={`bq-seg${gaps.includes("minors") ? " missing" : ""}`} data-testid="bq-minors">
        <Seg labelledBy="bq-minors" value={choice.minors} onChange={(v) => set({ minors: v })} options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]} />
      </div>
      {choice.minors === "yes" && (
        <>
          <label className={`tick${gaps.includes("guardian") ? " missing" : ""}`} id="bq-guardian">
            <input type="checkbox" checked={choice.guardian} onChange={(e) => set({ guardian: e.target.checked })} data-testid="bq-guardian" />
            <span><Words text={MINORS_PROMPT.text} /></span>
          </label>
          {MINORS_PROMPT.note && <p className="tiny tick-note">{MINORS_PROMPT.note}</p>}
        </>
      )}
    </>
  );
}

/** The scorecard link and the participant's name on it, both required: how the reel finds their balls. */
export function ScorecardFields({ choice, onChange, gaps }: Part) {
  const set = (p: Partial<BetaChoice>) => onChange({ ...choice, ...p });
  return (
    <>
      <div className="bq-pair">
        <div className="bq-wide">
          <label htmlFor="bq-score" className="bq-lbl">Scorecard link</label>
          <input id="bq-score" className={`field bq-field${gaps.includes("scorecard") ? " missing" : ""}`} type="url" inputMode="url" autoComplete="off" spellCheck={false}
            placeholder="Ball-by-ball scorecard link" value={choice.scorecard} onChange={(e) => set({ scorecard: e.target.value })} data-testid="bq-score" />
        </div>
        <div className="bq-narrow">
          <label htmlFor="bq-name" className="bq-lbl">Your name on the scorecard</label>
          <input id="bq-name" className={`field bq-field${gaps.includes("name") ? " missing" : ""}`} type="text" autoComplete="name"
            value={choice.name} onChange={(e) => set({ name: e.target.value })} data-testid="bq-name" />
        </div>
      </div>
      <p className="tiny bq-hint">The scorecard of this match, with your name on it.</p>
    </>
  );
}

/** Optional, explicitly ticked for each upload when granted; the Instagram handle remains optional (terms 5.1, 5.2, 5.4). */
export function SocialMediaFields({ choice, onChange, gaps }: Part) {
  const set = (p: Partial<BetaChoice>) => onChange({ ...choice, ...p });
  return (
    <>
      <label className={`tick${gaps.includes("social") ? " missing" : ""}`} id="bq-social">
        <input type="checkbox" checked={choice.social} aria-invalid={gaps.includes("social") || undefined} onChange={(e) => set({ social: e.target.checked })} data-testid="bq-social" />
        <span><Words text={SOCIAL_PROMPT.text} /></span>
      </label>
      {choice.social && (
        <div className="tick-more">
          <label htmlFor="bq-handle" className="bq-lbl first">Your Instagram handle <span className="opt">optional, to tag you</span></label>
          <div className={`bt-at${gaps.includes("handle") ? " missing" : ""}`}>
            <span aria-hidden="true">@</span>
            <input id="bq-handle" type="text" autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="yourhandle"
              value={choice.handle} onChange={(e) => set({ handle: e.target.value.replace(/^\s*@+/, "") })} data-testid="bq-handle" />
          </div>
        </div>
      )}
      {SOCIAL_PROMPT.note && <p className="tiny tick-note" data-testid="bq-social-note">{SOCIAL_PROMPT.note}</p>}
    </>
  );
}

/** Last before Submit: the sentence on the licence and the terms tick, or, once agreed, the day it was. */
export function TermsBlock({ state, choice, onChange, gaps }: Part & { state: BetaState | undefined }) {
  if (!needsTermsTick(state) && state?.accepted) {
    return (
      <p className="tiny bq-agreed" data-testid="bq-agreed">
        You agreed to the <DocLink kind="beta_terms" /> on {fmtDate(state.accepted.at)} · <DocLink kind="privacy" />
      </p>
    );
  }
  return (
    <section className="bq-terms" data-testid="bq-terms">
      {state?.accepted && (
        <p className="tiny bq-changed" data-testid="bq-terms-changed">
          The Terms of use changed on {fmtDate(`${DOCS.beta_terms.effective}T12:00:00Z`)}. Agree to version {DOCS.beta_terms.version} to submit.
        </p>
      )}
      {TERMS_PROMPT.lead && <p className="sub"><Words text={TERMS_PROMPT.lead} /></p>}
      <label className={`tick${gaps.includes("terms") ? " missing" : ""}`} id="bq-terms">
        <input type="checkbox" checked={choice.terms} onChange={(e) => onChange({ ...choice, terms: e.target.checked })} data-testid="bq-terms-tick" />
        <span><Words text={TERMS_PROMPT.text} /></span>
      </label>
    </section>
  );
}

const SENT_WORDS: Record<Submission["status"], { dot: "" | "on" | "ok" | "bad"; words: string }> = {
  uploading: { dot: "", words: "Upload not finished: choose the same file to resume it" },
  checking: { dot: "on", words: "Checking the video" },
  received: { dot: "ok", words: "Received" },
  expired: { dot: "", words: "Expired — the storage period has ended" },
  not_used: { dot: "bad", words: "We couldn't use this video" },
};

/** The videos sent so far, newest first: received, checking the video, or why we couldn't use it. */
export function SentSoFar({ items, title = "Sent so far" }: { items: Submission[]; title?: string }) {
  if (!items.length) return null;
  return (
    <section className="bq-sent" data-testid="bq-sent">
      <h2>{title}</h2>
      <div className="list mt10">
        {items.map((s) => {
          const w = SENT_WORDS[s.status];
          return (
            <div key={s.id} data-testid="bq-sent-row" data-status={s.status}>
              <div className="bq-sent-row">
                <div className="row between"><span className="med bq-sent-title">{s.title}</span><span className="tiny">{fmtDate(s.created_at)}</span></div>
                <div className="tiny mt6"><Dot kind={w.dot} />{w.words}{s.reason ? `. ${s.reason}` : ""}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
